import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { Button, Form, Message } from "semantic-ui-react";

import { PurClientConfig, PurRecord } from "react-lib/apis/PUR/purApi";
import { listPurLookup, PurLookupOption } from "react-lib/apis/PUR/purLookupsApi";
import { preparePurReceiptOrder, PurReceiptError, PurReceiptWriteAccess } from "react-lib/apis/PUR/purReceiptCreate";

import { DraftNotice, newPurchaseLine, PurchaseDraftForm, ReceiptDraftForm } from "./PurDraftForms";
import PurDocumentDraft, { emptyDocumentDraft } from "./PurDocumentDraft";
import PurDraftSave, { usePurDraftSave } from "./PurDraftSave";
import PurRequestOrderSave, { usePurRequestOrderSave } from "./PurRequestOrderSave";
import PurReceiptSave, { usePurReceiptSave } from "./PurReceiptSave";
import PurLookupField from "./PurLookupField";
import PurProductStock from "./PurProductStock";
import { emptyPurchaseDraft, emptyReceiptDraft, localDate, PurchaseDraft, receiptDraftFromOrder } from "./purDrafts";
import { formatMeerpMessage } from "./purPresentation";

export interface MeERPPhaseBHandle {
  prepareReceipt: (po: PurRecord) => void;
  prepareOrderFromRequest: (draft: PurchaseDraft) => void;
}
interface MeERPPhaseBProps { client: PurClientConfig; screen: string; documentDraft: boolean; receiptWriteAccess?: PurReceiptWriteAccess; onOpenReceipt?: (record: PurRecord) => void; onInspectReceipt?: (id: number | null) => void; onOpenSaved?: (kind: "materialRequests" | "purchaseOrders", record: PurRecord) => void; onOpenPurchaseDraft?: () => void }

// This owner remains mounted across navigation. Its parent remounts on auth,
// host, PUR prefix or division changes, so no draft crosses an API context.
const MeERPPhaseB = forwardRef<MeERPPhaseBHandle, MeERPPhaseBProps>(({ client, screen, documentDraft, receiptWriteAccess, onOpenReceipt, onInspectReceipt, onOpenSaved, onOpenPurchaseDraft }, ref) => {
  const intl = useIntl();
  const t = (id: string) => formatMeerpMessage(intl, id);
  const [request, setRequest] = useState(() => emptyPurchaseDraft("materialRequests"));
  const [order, setOrder] = useState(() => emptyPurchaseDraft("purchaseOrders"));
  const [kind, setKind] = useState<"materialRequests" | "purchaseOrders">("materialRequests");
  const [receipt, setReceipt] = useState(emptyReceiptDraft);
  const [pendingOrder, setPendingOrder] = useState<PurRecord | null>(null);
  const [pendingRequestOrder, setPendingRequestOrder] = useState<PurchaseDraft | null>(null);
  const [pendingCatalogueDraft, setPendingCatalogueDraft] = useState<PurchaseDraft | null>(null);
  const [requestOrderError, setRequestOrderError] = useState<string | null>(null);
  const [document, setDocument] = useState(emptyDocumentDraft);
  const [medicine, setMedicine] = useState<PurLookupOption | null>(null);
  const [resetRequested, setResetRequested] = useState<string | null>(null);
  const [draftRevision, setDraftRevision] = useState(0);
  const [receiptPreparing, setReceiptPreparing] = useState(false);
  const [receiptPrepareError, setReceiptPrepareError] = useState<string | null>(null);
  const preparation = useRef({ generation: 0, busy: false, active: true });
  useEffect(() => {
    const owner = preparation.current;
    owner.active = true; owner.generation += 1;
    return () => { owner.active = false; owner.generation += 1; };
  }, [client]);
  const requestSave = usePurDraftSave(client, request);
  const orderSave = usePurDraftSave(client, order);
  const linkedSave = usePurRequestOrderSave(client, order);
  const receiptSave = usePurReceiptSave(client, receipt, receiptWriteAccess);
  const purchaseSave = kind === "materialRequests" ? requestSave : orderSave;
  const orderCanReset = orderSave.canReset && linkedSave.canReset;
  const purchaseCanReset = kind === "materialRequests" ? requestSave.canReset : orderCanReset;
  const purchaseLocked = purchaseSave.locked || (kind === "purchaseOrders" && linkedSave.locked);
  const prepareReceipt = useCallback(async (po: PurRecord) => {
    if (!receiptSave.canReset || preparation.current.busy) { setReceiptPrepareError("MEERP_RECEIPT_REPLACE_LOCKED"); return; }
    const owner = preparation.current, generation = owner.generation;
    const isCurrent = () => owner.active && owner.generation === generation;
    owner.busy = true; setReceiptPreparing(true); setReceiptPrepareError(null); setPendingOrder(null); setResetRequested(null);
    try {
      const latest = await preparePurReceiptOrder(client, po.id);
      if (!isCurrent()) return;
      if (receipt.po) setPendingOrder(latest);
      else {
        const draft = receiptDraftFromOrder(latest);
        // Resolve a proposed warehouse through the real picker; never fabricate
        // a lookup option from the document header. A lookup failure leaves it
        // empty so the user can select a warehouse explicitly.
        if (latest.storageId) {
          try {
            const choices = await listPurLookup(client, "storages", { limit: 20, offset: 0, search: "" });
            draft.storage = choices.records.find((option) => String(option.id) === latest.storageId) || null;
          } catch { draft.storage = null; }
        }
        if (isCurrent()) { receiptSave.reset(); setReceipt(draft); }
      }
    } catch (error: unknown) {
      if (isCurrent()) setReceiptPrepareError(error instanceof PurReceiptError ? error.messageId : "MEERP_RECEIPT_CHECK_ERROR");
    } finally {
      if (isCurrent()) { owner.busy = false; setReceiptPreparing(false); }
    }
  }, [client, receipt.po, receiptSave.canReset, receiptSave.reset]);
  const installRequestOrder = (draft: PurchaseDraft) => {
    if (!orderCanReset) { setRequestOrderError("MEERP_MR_PO_REPLACE_LOCKED"); return; }
    orderSave.reset(); linkedSave.reset(); setOrder(draft); setKind("purchaseOrders");
    setPendingRequestOrder(null); setRequestOrderError(null); setResetRequested(null);
    setDraftRevision((revision) => revision + 1);
  };
  const hasPurchaseDraft = (draft: PurchaseDraft): boolean => Boolean(draft.project || draft.storage || draft.vendor || draft.purpose || draft.notes
    || draft.lines.length || draft.deliveryDate || draft.discount || draft.vatRate || draft.sourceRequest);
  const installCatalogueDraft = (draft: PurchaseDraft) => {
    if (draft.kind === "materialRequests") {
      requestSave.reset(); setRequest(draft);
    } else {
      orderSave.reset(); linkedSave.reset(); setOrder(draft);
    }
    setKind(draft.kind); setPendingCatalogueDraft(null); setPendingRequestOrder(null); setRequestOrderError(null); setResetRequested(null);
    setDraftRevision((revision) => revision + 1);
  };
  const prepareCatalogueDraft = (draftKind: "materialRequests" | "purchaseOrders") => {
    if (!medicine) return;
    const canReset = draftKind === "materialRequests" ? requestSave.canReset : orderCanReset;
    const draft = { ...emptyPurchaseDraft(draftKind), lines: [newPurchaseLine(medicine, 1)] };
    setKind(draftKind); setPendingCatalogueDraft(null); setPendingRequestOrder(null); setRequestOrderError(null); setResetRequested(null); onOpenPurchaseDraft?.();
    if (!canReset) { setRequestOrderError("MEERP_CATALOGUE_DRAFT_LOCKED"); return; }
    if (hasPurchaseDraft(draftKind === "materialRequests" ? request : order)) setPendingCatalogueDraft(draft);
    else installCatalogueDraft(draft);
  };
  const prepareOrderFromRequest = (draft: PurchaseDraft) => {
    if (draft.kind !== "purchaseOrders" || !draft.sourceRequest) return;
    setKind("purchaseOrders"); setResetRequested(null); setPendingRequestOrder(null); setRequestOrderError(null);
    if (!orderCanReset) { setRequestOrderError("MEERP_MR_PO_REPLACE_LOCKED"); return; }
    const hasDraft = order.sourceRequest || order.project || order.storage || order.vendor || order.lines.length || order.purpose || order.notes
      || order.deliveryDate || order.discount || order.vatRate || order.date !== localDate() || orderSave.state.phase === "saved";
    if (hasDraft) setPendingRequestOrder(draft);
    else installRequestOrder(draft);
  };
  useImperativeHandle(ref, () => ({ prepareReceipt, prepareOrderFromRequest }));
  const visible = ["MD01", "PR02", "GR02"].includes(screen) || (screen === "RP01" && documentDraft);
  if (!visible) return null;
  const draftKey = `${screen}:${kind}`;
  return <section className="card-meerp__phase-b">
    <div className="card-meerp__page-heading"><div><h2>{t(`MEERP_SCREEN_${screen}`)}</h2></div></div>
    {screen === "MD01" ? <>
      <p>{t("MEERP_DRAFT_CATALOGUE_NOTE")}</p>
      <Form onSubmit={(event) => event.preventDefault()}><PurLookupField id="catalogue-medicine" client={client} kind="products" label={t("MEERP_PRODUCT")} value={medicine} onChange={setMedicine} /></Form>
      {medicine && <dl className="card-meerp__facts">
        <div><dt>{t("MEERP_PRODUCT_ID")}</dt><dd>{medicine.id}</dd></div>
        <div><dt>{t("MEERP_PRODUCT")}</dt><dd>{medicine.label}</dd></div>
        <div><dt>{t("MEERP_DRAFT_PRODUCT_TYPE")}</dt><dd>{medicine.productType || "—"}</dd></div>
        <div><dt>{t("MEERP_UNIT")}</dt><dd>{medicine.unitName || (medicine.unitId ? `#${medicine.unitId}` : "—")}</dd></div>
        <div><dt>{t("MEERP_DRAFT_MASTER_COST")}</dt><dd>{medicine.cost ?? "—"}</dd></div>
        <div><dt>{t("MEERP_DRAFT_MASTER_SALE")}</dt><dd>{medicine.salePrice ?? "—"}</dd></div>
      </dl>}
      {medicine && <div className="card-meerp__action-bar">
        <Button type="button" onClick={() => prepareCatalogueDraft("materialRequests")}>{t("MEERP_CATALOGUE_DRAFT_REQUEST")}</Button>
        <Button type="button" onClick={() => prepareCatalogueDraft("purchaseOrders")}>{t("MEERP_CATALOGUE_DRAFT_ORDER")}</Button>
      </div>}
      <PurProductStock client={client} productId={medicine?.id ?? null} />
    </> : <>
      {screen === "PR02" && kind === "purchaseOrders" && order.sourceRequest ? <Message info className="card-meerp__draft-notice">
        <p>{formatMeerpMessage(intl, linkedSave.state.result ? "MEERP_MR_PO_SOURCE_SAVED" : "MEERP_MR_PO_SOURCE", { number: order.sourceRequest.number, id: order.sourceRequest.id })}</p>
        <p>{t("MEERP_MR_PO_SNAPSHOT_NOTE")}</p>
        <p>{t("MEERP_DRAFT_MEMORY")}</p>
      </Message> : screen === "GR02" ? <Message info><p>{t("MEERP_RECEIPT_DRAFT_NOTICE")}</p><p>{t("MEERP_DRAFT_MEMORY")}</p></Message> : <DraftNotice procurement={screen === "PR02"} />}
      <Button type="button" size="small" disabled={(screen === "PR02" && (!purchaseCanReset || Boolean(pendingRequestOrder))) || (screen === "GR02" && (!receiptSave.canReset || receiptPreparing))} onClick={() => setResetRequested(draftKey)}>{t("MEERP_DRAFT_NEW")}</Button>
      {resetRequested === draftKey && <Message warning>
        <p>{t("MEERP_DRAFT_RESET_WARNING")}</p>
        <Button type="button" onClick={() => {
          if (screen === "PR02") {
            if (!purchaseCanReset) return;
            purchaseSave.reset();
            (kind === "materialRequests" ? setRequest : setOrder)(emptyPurchaseDraft(kind));
            if (kind === "purchaseOrders") { linkedSave.reset(); setPendingRequestOrder(null); setRequestOrderError(null); }
          }
          if (screen === "GR02") {
            if (!receiptSave.canReset || receiptPreparing) return;
            receiptSave.reset(); setReceipt(emptyReceiptDraft()); setPendingOrder(null); setReceiptPrepareError(null);
          }
          if (screen === "RP01") setDocument(emptyDocumentDraft());
          setDraftRevision((revision) => revision + 1); setResetRequested(null);
        }}>{t("MEERP_DRAFT_RESET_CONFIRM")}</Button>
        <Button type="button" onClick={() => setResetRequested(null)}>{t("MEERP_DRAFT_RESET_CANCEL")}</Button>
      </Message>}
      {screen === "GR02" && pendingOrder && <Message warning>
        <p>{intl.formatMessage({ id: "MEERP_DRAFT_REPLACE_PO_WARNING" }, { number: pendingOrder.number })}</p>
        <Button type="button" disabled={!receiptSave.canReset || receiptPreparing} onClick={() => {
          if (!receiptSave.canReset || receiptPreparing) return;
          receiptSave.reset(); setReceipt(receiptDraftFromOrder(pendingOrder)); setPendingOrder(null); setDraftRevision((revision) => revision + 1);
        }}>{t("MEERP_DRAFT_REPLACE_PO_CONFIRM")}</Button>
        <Button type="button" onClick={() => setPendingOrder(null)}>{t("MEERP_DRAFT_RESET_CANCEL")}</Button>
      </Message>}
      {screen === "PR02" && <>
        {requestOrderError && <Message negative role="alert">{t(requestOrderError)}</Message>}
        {pendingCatalogueDraft && <Message warning role="alert">
          <p>{formatMeerpMessage(intl, "MEERP_CATALOGUE_DRAFT_REPLACE_NOTE", { product: pendingCatalogueDraft.lines[0].product.label })}</p>
          <Button type="button" disabled={pendingCatalogueDraft.kind === "materialRequests" ? !requestSave.canReset : !orderCanReset} onClick={() => installCatalogueDraft(pendingCatalogueDraft)}>{t("MEERP_CATALOGUE_DRAFT_REPLACE_CONFIRM")}</Button>
          <Button type="button" onClick={() => setPendingCatalogueDraft(null)}>{t("MEERP_DRAFT_RESET_CANCEL")}</Button>
        </Message>}
        {kind === "purchaseOrders" && pendingRequestOrder?.sourceRequest && <Message warning role="alert">
          <p>{formatMeerpMessage(intl, "MEERP_MR_PO_REPLACE_NOTE", { number: pendingRequestOrder.sourceRequest.number })}</p>
          <Button type="button" disabled={!orderCanReset} onClick={() => installRequestOrder(pendingRequestOrder)}>{t("MEERP_MR_PO_REPLACE_CONFIRM")}</Button>
          <Button type="button" onClick={() => setPendingRequestOrder(null)}>{t("MEERP_DRAFT_RESET_CANCEL")}</Button>
        </Message>}
        <div className="card-meerp__tabs" role="group" aria-label={t("MEERP_SCREEN_PR02")}>
          <button className="card-meerp__tab" type="button" disabled={Boolean(pendingRequestOrder || pendingCatalogueDraft)} aria-pressed={kind === "materialRequests"} onClick={() => setKind("materialRequests")}>{t("MEERP_MATERIAL_REQUESTS")}</button>
          <button className="card-meerp__tab" type="button" disabled={Boolean(pendingRequestOrder || pendingCatalogueDraft)} aria-pressed={kind === "purchaseOrders"} onClick={() => setKind("purchaseOrders")}>{t("MEERP_PURCHASE_ORDERS")}</button>
        </div>
        <PurchaseDraftForm key={`${kind}:${draftRevision}`} client={client} value={kind === "materialRequests" ? request : order}
          locked={purchaseLocked || Boolean(pendingRequestOrder || pendingCatalogueDraft)} onChange={(next) => { if (!purchaseLocked && !pendingRequestOrder && !pendingCatalogueDraft) (kind === "materialRequests" ? setRequest : setOrder)(next); }}
          saveControls={pendingRequestOrder || pendingCatalogueDraft ? <p>{t(pendingCatalogueDraft ? "MEERP_CATALOGUE_DRAFT_REPLACE_PENDING" : "MEERP_MR_PO_REPLACE_PENDING")}</p> : kind === "purchaseOrders" && order.sourceRequest
            ? <PurRequestOrderSave client={client} controller={linkedSave} onOpenSaved={(record) => onOpenSaved?.("purchaseOrders", record)} />
            : <PurDraftSave client={client} value={kind === "materialRequests" ? request : order} controller={purchaseSave} onOpenSaved={(record) => onOpenSaved?.(kind, record)} />} />
      </>}
      {screen === "GR02" && <>
        {receiptPreparing && <Message info role="status">{t("MEERP_RECEIPT_PREPARING")}</Message>}
        {receiptPrepareError && <Message negative role="alert">{t(receiptPrepareError)}</Message>}
        <ReceiptDraftForm key={draftRevision} client={client} value={receipt} locked={receiptSave.locked || receiptPreparing || Boolean(pendingOrder)}
          onChange={(next) => { if (!receiptSave.locked && !receiptPreparing && !pendingOrder) setReceipt(next); }} onSelectOrder={prepareReceipt}
          saveControls={pendingOrder || receiptPreparing ? <p>{t(pendingOrder ? "MEERP_RECEIPT_REPLACE_PENDING" : "MEERP_RECEIPT_PREPARING")}</p> : <PurReceiptSave client={client} controller={receiptSave} onOpenSaved={onOpenReceipt} onInspectReceipt={onInspectReceipt} />} />
      </>}
      {screen === "RP01" && <PurDocumentDraft key={draftRevision} client={client} value={document} onChange={setDocument} />}
    </>}
  </section>;
});

export default MeERPPhaseB;
